import { CATEGORIES } from '../tasks.js';
import { calcPercent, formatDate } from '../storage.js';

const W = 1080, H = 1080;

function drawRoundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

async function drawCard(tasks, dayData) {
  await document.fonts.ready;

  const canvas = document.createElement('canvas');
  canvas.width  = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  // Background
  ctx.fillStyle = '#FDF6F0';
  ctx.fillRect(0, 0, W, H);

  // Subtle grid texture
  ctx.strokeStyle = 'rgba(44,36,32,0.03)';
  ctx.lineWidth = 1;
  for (let x = 0; x < W; x += 40) {
    ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
  }
  for (let y = 0; y < H; y += 40) {
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
  }

  const pad  = 72;
  const cw   = W - pad * 2;

  // Date eyebrow
  const today = new Date();
  const dayStr = today.toLocaleDateString('uk-UA', { weekday: 'long', day: 'numeric', month: 'long' })
    .replace(/^./, c => c.toUpperCase());

  ctx.fillStyle = '#9A8880';
  ctx.font = '400 28px "DM Sans", sans-serif';
  ctx.letterSpacing = '2px';
  ctx.fillText('МІЙ ДЕНЬ', pad, pad + 36);
  ctx.letterSpacing = '0px';

  ctx.fillStyle = '#2C2420';
  ctx.font = '500 72px "Playfair Display", serif';
  ctx.fillText(dayStr, pad, pad + 108);

  // Divider
  ctx.fillStyle = 'rgba(44,36,32,0.08)';
  ctx.fillRect(pad, pad + 130, cw, 1.5);

  // Percent circle
  const pct    = calcPercent(tasks, dayData);
  const cx     = W - pad - 90;
  const cy     = pad + 80;
  const radius = 70;

  // Circle BG
  ctx.strokeStyle = '#F0E8E4';
  ctx.lineWidth   = 10;
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.stroke();

  // Circle fill
  if (pct > 0) {
    const start = -Math.PI / 2;
    const end   = start + (pct / 100) * Math.PI * 2;
    ctx.strokeStyle = '#C9907C';
    ctx.lineWidth   = 10;
    ctx.lineCap     = 'round';
    ctx.beginPath();
    ctx.arc(cx, cy, radius, start, end);
    ctx.stroke();
    ctx.lineCap = 'butt';
  }

  ctx.fillStyle = '#C9907C';
  ctx.font = 'italic 500 36px "Playfair Display", serif';
  ctx.textAlign = 'center';
  ctx.fillText(`${pct}%`, cx, cy + 12);
  ctx.textAlign = 'left';

  // Done tasks
  const doneTasks = tasks.filter(t => {
    const checked = !!dayData[t.id];
    return t.isAnti ? !checked : checked;
  });

  let yPos = pad + 180;
  const lineH = 68;

  doneTasks.slice(0, 10).forEach(task => {
    const cat    = CATEGORIES[task.category];
    const catHex = cat?.hex || '#F2C4B0';

    // Pill background
    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    drawRoundRect(ctx, pad, yPos, cw, 52, 12);
    ctx.fill();

    // Category dot
    ctx.fillStyle = catHex;
    ctx.beginPath();
    ctx.arc(pad + 28, yPos + 26, 6, 0, Math.PI * 2);
    ctx.fill();

    // Checkmark circle
    ctx.fillStyle = '#7BAF8E';
    ctx.beginPath();
    ctx.arc(cw + pad - 28, yPos + 26, 14, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = '#fff';
    ctx.lineWidth   = 2.5;
    ctx.lineCap     = 'round';
    ctx.lineJoin    = 'round';
    ctx.beginPath();
    ctx.moveTo(cw + pad - 35, yPos + 26);
    ctx.lineTo(cw + pad - 29, yPos + 32);
    ctx.lineTo(cw + pad - 21, yPos + 20);
    ctx.stroke();

    // Task label
    ctx.fillStyle = '#2C2420';
    ctx.font = '400 30px "DM Sans", sans-serif';
    ctx.fillText(task.label, pad + 52, yPos + 32);

    yPos += lineH;
  });

  // Footer
  ctx.fillStyle = 'rgba(154,136,128,0.5)';
  ctx.fillRect(pad, H - pad - 1, cw, 1);

  ctx.fillStyle = '#9A8880';
  ctx.font = '400 24px "DM Sans", sans-serif';
  ctx.fillText('My Routine', pad, H - pad + 28);

  ctx.textAlign = 'right';
  ctx.fillStyle = '#C9907C';
  ctx.font = 'italic 500 26px "Playfair Display", serif';
  ctx.fillText(`${doneTasks.length} з ${tasks.length} виконано`, W - pad, H - pad + 28);
  ctx.textAlign = 'left';

  return canvas;
}

export async function shareDay(tasks, dayData) {
  try {
    const canvas = await drawCard(tasks, dayData);

    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));

    if (navigator.share && navigator.canShare?.({ files: [new File([blob], 'routine.png', { type: 'image/png' })] })) {
      const file = new File([blob], 'routine.png', { type: 'image/png' });
      await navigator.share({ files: [file], title: 'Мій день', text: 'Мій щоденний ритуал' });
    } else {
      // Desktop fallback: download
      const url = URL.createObjectURL(blob);
      const a   = document.createElement('a');
      a.href     = url;
      a.download = `routine-${formatDate()}.png`;
      a.click();
      URL.revokeObjectURL(url);
    }
  } catch (err) {
    if (err.name !== 'AbortError') console.error('Share failed', err);
  }
}
